import localStorageRepositories from "@/plugins/localStorageRepositories";
import { resolveClosedCampaignThumbnail } from "@/helpers/closedCampaignThumbnail";
import {
  getCampaignCircleItemWidth,
  getCampaignGroupGap,
  getCampaignReadStatusBorderWidth,
  getCampaignTileColumns,
  getCampaignTileNaturalWidth,
  getRenderedCampaignGroupGap,
  TILE_DEFAULT_GAP,
  TILE_REFERENCE_HEIGHT,
} from "@/helpers/campaignSettings";
import { queryStoreeelAll } from "@/helpers/storeeelShadowDom";
import { isReelLike } from "@/helpers/isReelLike";
import { escapeCssUrl, escapeHtml, escapeUrlAttribute } from "@/helpers/escapeHtml";
import {
  buildFeedFolderSaleLabelHtml,
  normalizeFeedFolderSaleLabelColor,
  FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
} from "@/helpers/feedFolderSaleLabel";

const DEFAULT_CIRCLE_THUMBNAIL_SIZE = 80;
const DEFAULT_SQUARE_THUMBNAIL_WIDTH = 182;
const DEFAULT_SQUARE_THUMBNAIL_HEIGHT = 324;
const DEFAULT_PREVIEW_LOOP_SECONDS = 3;
const PREVIEW_LOOP_EPSILON_SECONDS = 0.03;
const previewVideos = new Set();
// サムネ画像が1枚だけ壊れていても、スマホの動画再生がいつまでも始まらないようにする上限
const THUMBNAIL_WAIT_TIMEOUT_MS = 5000;

const getPreviewLoopSeconds = () => {
  const configured = Number(window.storeeelItems?.previewLoopSeconds);
  return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_PREVIEW_LOOP_SECONDS;
};

const prefersReducedMotion = () => (
  typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const extractBackgroundImageUrl = (element) => {
  const match = /url\((['"]?)(.*?)\1\)/.exec(element?.style?.backgroundImage || '');
  return match?.[2] || '';
};

const waitForImageLoad = (url) => new Promise((resolve) => {
  const image = new Image();
  image.onload = resolve;
  image.onerror = resolve;
  image.src = url;
  if (image.complete) resolve();
});

// 背景画像として描画済みのサムネと同じURLを読み、全部揃った（または失敗した）時点で解決する
const waitForThumbnailImages = (imageElements) => {
  const urls = [...new Set(imageElements.map(extractBackgroundImageUrl).filter(Boolean))];
  if (urls.length === 0) return Promise.resolve();

  return Promise.race([
    Promise.all(urls.map(waitForImageLoad)),
    new Promise((resolve) => setTimeout(resolve, THUMBNAIL_WAIT_TIMEOUT_MS)),
  ]);
};

const buildDeferredVideoMarkup = (thumbnail, content, contentGroup, baseClass, videoStyle = '') => {
  if (!thumbnail?.playbackVideoUrl) return '';

  return `
    <video
      class="${baseClass} storeeel-content-movie"
      data-storeeel-preview-video="true"
      data-storeeel-content-id="${content.id}"
      preload="none"
      playsinline
      muted
      style="display: none;${videoStyle}"
      aria-label="${escapeHtml(contentGroup.name)}"
    ></video>
  `;
};

export const icons = (item) => {
  const campaign = item;
  const contentGroups = campaign.contentGroups;
  const unseenColor = campaign?.unreadColorCode || '#000000';
  const seenColor = campaign?.readColorCode || '#A3A3A3';
  const groupNameColor = campaign?.groupNameColorCode || campaign?.group_name_color_code || null;
  const groupNameFontFamily = campaign?.groupNameFontFamily || campaign?.group_name_font_family || null;
  const groupNameFontWeight = campaign?.groupNameFontWeight || campaign?.group_name_font_weight || null;
  const groupNameFontSize = campaign?.groupNameFontSize || campaign?.group_name_font_size || null;
  const thumbnailScale = campaign?.thumbnailScale || campaign?.thumbnail_scale || null;
  const showReadStatus = campaign?.isReadStatusVisible === true;
  const readStatusBorderWidth = getCampaignReadStatusBorderWidth(campaign);
  const circleIconBorder = showReadStatus ? 'none' : '1px solid #e0e0e0';
  const groupGap = getCampaignGroupGap(campaign);
  const circleItemWidth = getCampaignCircleItemWidth(campaign);
  const circleGroupNameWidth = circleItemWidth;
  const isTile = campaign?.frame === 'tile';
  // リール型は左右marginで10px分の間隔を作るため、設定値が小さい時はmargin側を縮める
  const squareContentHorizontalMargin = isReelLike(campaign?.frame) && groupGap != null
    ? Math.min(groupGap / 2, 5)
    : 5;

  // content_group ごとのタイル/アイコンHTMLを生成する（行ラップ等で再利用するため { id, tileRow, html } で返す）
  const buildGroupMarkups = (contentGroups) => {
    return contentGroups.map(contentGroup => {
      const contents = contentGroup.contents;
      if (contents.length === 0) {
        return { id: contentGroup.id, tileRow: 0, html: '' };
      }
      const content = contents[0];
      const thumbnail = resolveClosedCampaignThumbnail(campaign, contentGroup, content);
      // 先頭コンテンツが広告かどうかで、初期表示の補助ラベルを切り替える
      const isAdMediaAsset = content?.mediaAsset?.type === 'adMediaAsset';
      // グループ内の全コンテンツが既読か（無期限TTLの contentSeen を参照）
      const allSeen = contents.every(c => localStorageRepositories.get('contentSeen')?.has?.(c.id));
      let html = '';
      const groupNameStyleVars = [
        `--storeeel-unseen-border-color: ${unseenColor}`,
        `--storeeel-seen-border-color: ${seenColor}`,
        `--storeeel-read-status-ring-size: ${readStatusBorderWidth}px`,
      ];
      if (campaign?.frame === 'circle') {
        groupNameStyleVars.push(`--storeeel-circle-item-width: ${circleItemWidth}`);
        groupNameStyleVars.push(`--storeeel-circle-icon-border: ${circleIconBorder}`);
      }
      if (groupNameColor) {
        groupNameStyleVars.push(`--storeeel-group-name-color: ${groupNameColor}`);
      }
      if (groupNameFontFamily) {
        groupNameStyleVars.push(`--storeeel-group-name-font-family: ${groupNameFontFamily}`);
      }
      if (groupNameFontWeight) {
        groupNameStyleVars.push(`--storeeel-group-name-font-weight: ${groupNameFontWeight}`);
      }
      if (groupNameFontSize) {
        groupNameStyleVars.push(`--storeeel-group-name-font-size: ${Number(groupNameFontSize)}px`);
      }
      if (campaign?.frame === 'circle' && thumbnailScale) {
        const circleThumbnailSize = Math.round(DEFAULT_CIRCLE_THUMBNAIL_SIZE * Number(thumbnailScale));
        groupNameStyleVars.push(`--storeeel-circle-thumbnail-size: ${circleThumbnailSize}px`);
        groupNameStyleVars.push(`--storeeel-group-name-width: ${Math.max(circleThumbnailSize, 92)}px`);
      }
      // リール型・タイル型はサムネイル寸法CSS変数を設定する（ビューア内で参照される）
      if (isReelLike(campaign?.frame) && thumbnailScale) {
        const squareThumbnailWidth = Math.round(DEFAULT_SQUARE_THUMBNAIL_WIDTH * Number(thumbnailScale));
        const squareThumbnailHeight = Math.round(DEFAULT_SQUARE_THUMBNAIL_HEIGHT * Number(thumbnailScale));
        groupNameStyleVars.push(`--storeeel-square-thumbnail-width: ${squareThumbnailWidth}px`);
        groupNameStyleVars.push(`--storeeel-square-thumbnail-height: ${squareThumbnailHeight}px`);
        groupNameStyleVars.push(`--storeeel-group-name-width: ${Math.max(squareThumbnailWidth, 92)}px`);
      }
      const groupNameStyle = groupNameStyleVars.join('; ');

      // グループ名は連携配信で商品名にフォールバックしうる（feedFolderResolver.js）。
      // GMC由来の文字列がそのまま属性値に入るため必ずエスケープする。
      const groupNameAttr = escapeHtml(contentGroup.name);
      if (campaign.frame === "circle") {
        html += `<div class="storeeel_campaign storeeel-icon campaign_${campaign.id} storeeel_campaign_circle" id="storeeel-icon-${contentGroup.id}" data-type="${groupNameAttr}" group-name="${groupNameAttr}" style="${groupNameStyle};">`
      } else {
        html += `<div class="storeeel_campaign storeeel-icon-square campaign_${campaign.id}" id="storeeel-icon-${contentGroup.id}" data-type="${groupNameAttr}" group-name="${groupNameAttr}" style="${groupNameStyle};">`
      }

      if (isReelLike(campaign.frame)) {
        // リール型・タイル型の埋め込み表示
        const adBadgeHtml = isAdMediaAsset
          ? `
            <div class="storeeel-square-ad-badge">
              <div class="storeeel-square-ad-badge__text">PR</div>
            </div>
          `
          : '';
        // 連携配信のセール商品ラベル。「設定がON」かつ「セール中」の判定と中身の組み立ては
        // feedFolderResolver.js が content.showSaleLabel / saleLabel に畳んでいるため、ここでは見るだけ。
        // circle は丸いサムネイルで角にバッジを置けないため、広告バッジと同じくsquare/tileのみ。
        // 配色だけは管理画面で選べるため、クラスの既定色をインラインで上書きする
        // （クラスは1つでも、連携配信ごとに色が違いうる）。
        const saleBadgeHtml = content?.showSaleLabel
          ? `
            <div class="storeeel-square-sale-badge" style="background:${escapeHtml(normalizeFeedFolderSaleLabelColor(
              content?.saleLabelBackgroundColor,
              FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
            ))};">
              <div class="storeeel-square-sale-badge__text" style="color:${escapeHtml(normalizeFeedFolderSaleLabelColor(
                content?.saleLabelTextColor,
                FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
              ))};">${buildFeedFolderSaleLabelHtml(content.saleLabel)}</div>
            </div>
          `
          : '';
        if (thumbnail?.kind === 'image') {
          html += `
            <div
              class="storeeel-square-content-wrapper"
              ${thumbnail.playbackVideoUrl ? `data-storeeel-video-src="${escapeUrlAttribute(thumbnail.playbackVideoUrl)}"` : ''}
            >
              <div
                class="storeeel-content storeeel-content-image"
                id="storeeel-content-${content.id}"
                data-storeeel-preview-image="true"
                style="background-image: url('${escapeCssUrl(thumbnail.url)}'); background-size: cover; background-position: center; background-repeat: no-repeat;"
                role="img"
                aria-label="${groupNameAttr}"
              ></div>
              ${buildDeferredVideoMarkup(thumbnail, content, contentGroup, 'storeeel-content', '')}
              ${adBadgeHtml}
              ${saleBadgeHtml}
            </div>
          `;
        } else if (thumbnail?.kind === 'video') {
          html += `
            <div class="storeeel-square-content-wrapper">
              <video
                class="storeeel-content storeeel-content-movie"
                id="storeeel-content-${content.id}"
                src="${escapeUrlAttribute(thumbnail.url)}#t=0.1"
                playsinline
                muted
              ></video>
              ${adBadgeHtml}
              ${saleBadgeHtml}
            </div>
          `;
        }

        // タイル型はグループ名を表示しない（ADR-007）
        if (!isTile) {
          html += `
            <div class="storeeel_campaign_group_name">${groupNameAttr}</div>
          `;
        }
      } else {
        // ストーリー型のSALEラベルは丸いサムネイルの内側下部へ重ねる（グループ名の下ではない）。
        // 基準にできるサイズを持つのは .storeeel-icon（丸そのもの）なので、その中に入れる。
        // フィードフォルダの商品は必ず静止画のため、動画分岐には現れない。
        //
        // ここだけは表示形式（saleLabelType）によらず常に 'SALE'。丸の内側という
        // 最も幅の取れない場所で、価格や割引率を入れると文字が潰れて読めなくなるため。
        // 形式を選んだ効果はスライド本体と square/tile のサムネに出る。
        // 配色だけは管理画面で選べるため、クラスの既定色をインラインで上書きする
        // （クラスは1つでも、連携配信ごとに色が違いうる）。
        const circleSaleLabelStyle = content?.showSaleLabel
          ? `background:${escapeHtml(normalizeFeedFolderSaleLabelColor(
            content?.saleLabelBackgroundColor,
            FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
          ))};color:${escapeHtml(normalizeFeedFolderSaleLabelColor(
            content?.saleLabelTextColor,
            FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
          ))};`
          : '';
        const circleSaleLabelHtml = content?.showSaleLabel
          ? `<div class="storeeel_campaign_sale_label" style="${circleSaleLabelStyle}">SALE</div>`
          : '';
        if (thumbnail?.kind === 'image') {
          // 円形アイコン画像の余白を常に無効化する
          if (showReadStatus) {
            html += `
              <div
                class="storeeel-group-border ${allSeen ? 'storeeel-group-seen' : 'storeeel-group-unseen'}"
                ${thumbnail.playbackVideoUrl ? `data-storeeel-video-src="${escapeUrlAttribute(thumbnail.playbackVideoUrl)}"` : ''}
              >
                <div
                  class="storeeel-icon storeeel-content-image"
                  id="storeeel-content-${content.id}"
                  data-storeeel-preview-image="true"
                  style="background-image: url('${escapeCssUrl(thumbnail.url)}'); background-size: cover; background-position: center; background-repeat: no-repeat; display: block; padding: 0;"
                  role="img"
                  aria-label="${groupNameAttr}"
                >${circleSaleLabelHtml}</div>
                ${buildDeferredVideoMarkup(thumbnail, content, contentGroup, 'storeeel-icon', 'border-radius: 50%;object-fit: cover;')}
              </div>
            `;
          } else {
            html += `
              <div ${thumbnail.playbackVideoUrl ? `data-storeeel-video-src="${escapeUrlAttribute(thumbnail.playbackVideoUrl)}"` : ''}>
                <div
                  class="storeeel-icon storeeel-content-image"
                  id="storeeel-content-${content.id}"
                  data-storeeel-preview-image="true"
                  style="background-image: url('${escapeCssUrl(thumbnail.url)}'); background-size: cover; background-position: center; background-repeat: no-repeat; display: block; padding: 0;"
                  role="img"
                  aria-label="${groupNameAttr}"
                >${circleSaleLabelHtml}</div>
                ${buildDeferredVideoMarkup(thumbnail, content, contentGroup, 'storeeel-icon', 'border-radius: 50%;object-fit: cover;')}
              </div>
            `;
          }
        } else if (thumbnail?.kind === 'video') {
          if (showReadStatus) {
            html += `
              <div class="storeeel-group-border ${allSeen ? 'storeeel-group-seen' : 'storeeel-group-unseen'}">
                <video
                  class="storeeel-icon storeeel-content-movie"
                  id="storeeel-content-${content.id}"
                  src="${escapeUrlAttribute(thumbnail.url)}#t=0.1"
                  playsinline
                  muted
                  style="border-radius: 50%;object-fit: cover; display: block;"
                ></video>
              </div>
            `;
          } else {
            html += `
              <video
                class="storeeel-icon storeeel-content-movie"
                id="storeeel-content-${content.id}"
                src="${escapeUrlAttribute(thumbnail.url)}#t=0.1"
                playsinline
                muted
                style="border-radius: 50%;object-fit: cover; display: block;"
              ></video>
            `;
          }
        }

        html += `
          <div class="storeeel_campaign_group_name">${groupNameAttr}</div>
        `;
        if (isAdMediaAsset) {
          html += `
            <div class="storeeel_campaign_ad_label">【PR】</div>
          `;
        }
      }

      html += `</div>`;
      // tileRow/tileCol は 0 始まりの所属行・列番号（camelCase/snake_case 両対応、既定 0）
      // ※詰めロジックの並び替え基準としてのみ使用し、描画上の行番号としては使わない
      const tileRow = Number(contentGroup?.tileRow ?? contentGroup?.tile_row ?? 0) || 0;
      const tileCol = Number(contentGroup?.tileCol ?? contentGroup?.tile_col ?? 0) || 0;
      return { id: contentGroup.id, tileRow, tileCol, html };
    });
  };

  // 従来通り全 content_group を連結した文字列を返す（reel/story 用、タイル型の1行フォールバックでも使用）
  const generateIcons = (contentGroups) => {
    return buildGroupMarkups(contentGroups).map(group => group.html).join('');
  };

  // タイル型は行コンテナ(.storeeel-tile-row)が CSS変数 --storeeel-tile-width で各タイル幅を渡す。
  // 幅は列の持ち分（画面内に収まる大きさ）で頭打ちにしつつ、高さは常に aspect-ratio で
  // 実際の幅から算出する。これにより、頭打ちで幅が縮んだ場合でも設定した縦横比は必ず維持され、
  // コンテンツの表示自体が（縦横比を保ったまま）小さくなる形になる。
  // 行内は常に左上起点（有効なコンテンツを左から詰める）で並べる。キャンペーン全体の
  // PC中央寄せ/モバイル左寄せは、グリッド全体の幅（.storeeell_base_X の max-width）を
  // getCampaignTileBlockMaxWidth で頭打ちさせたうえで、外側コンテナ側の中央寄せ処理に委ねる
  const tileStyle = () => {
    const aspectWidth = campaign?.tileAspectWidth ?? campaign?.tile_aspect_width ?? 9;
    const aspectHeight = campaign?.tileAspectHeight ?? campaign?.tile_aspect_height ?? 16;
    return `
    .storeeel-tile-row {
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-items: flex-start;
      gap: var(--storeeel-tile-gap, ${TILE_DEFAULT_GAP}px);
      box-sizing: border-box;
      max-width: 100%;
    }
    .storeeel-tile-row .storeeel_campaign {
      /* 行内のタイル幅は親が渡す変数で確定し、行幅にちょうど収める（アスペクト比から求まる自然な幅で頭打ち）。
         高さは aspect-ratio により実際の幅から算出するため、頭打ちされても縦横比は保たれる */
      flex: 0 0 auto;
      width: var(--storeeel-tile-width, ${TILE_REFERENCE_HEIGHT * (9 / 16)}px);
      max-width: var(--storeeel-tile-width, ${TILE_REFERENCE_HEIGHT * (9 / 16)}px);
      aspect-ratio: ${aspectWidth} / ${aspectHeight};
    }
    .storeeel-tile-row .storeeel-square-content-wrapper {
      display: block;
      width: 100%;
      height: 100%;
    }
    .storeeel-tile-row .storeeel-content {
      width: 100%;
      height: 100%;
      margin: 0;
    }
  `;
  };

  const style = () => {
    // リール型・タイル型は縦長サムネのスタイルを適用する
    if (isReelLike(campaign.frame)) {
      return `
        .storeeel_campaign {
          flex: 0 0 auto;
          position: relative;
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 7px;
        }
        .storeeel-square-content-wrapper {
          position: relative;
          display: inline-block;
          line-height: 0;
        }
        .storeeel-content {
          display: block;
          border-radius: 10px;
          height: var(--storeeel-square-thumbnail-height, 324px);
          width: var(--storeeel-square-thumbnail-width, 182px);
          margin: 0px ${squareContentHorizontalMargin}px;
          cursor: pointer;
        }${isTile ? tileStyle() : ''}
        .storeeel-content-image {
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        .storeeel-content-movie {
          max-width: none;
          object-fit: cover;
        }
        .storeeel-square-ad-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0 8px;
          /* 箱の高さは固定する。上下paddingやフォント本来の行送りに任せると、埋め込み先の
             font-family（バッジは指定を持たず継承する）で高さが変わり、サイトごとにラベルの
             大きさが違って見える。9px * 1.5em（line-height: normal の最大）でも収まる値。 */
          height: 14px;
          border-radius: 999px;
          background: rgba(0, 0, 0, 0.40);
          z-index: 20;
          pointer-events: none;
        }
        .storeeel-square-ad-badge__text {
          color: #fff;
          /* 非viewer埋め込み時の広告バッジ文字は主張を抑える */
          font-size: 9px;
          font-style: normal;
          font-weight: 500;
          /* 行ボックスも font-size と同じ高さに固定する。normal だとフォント固有の行送りになり、
             上下非対称なぶんだけ文字が箱の中でずれる。1 にすれば上下の half-leading が均等に
             削られ、align-items: center と合わせて文字が箱の中央に来る。
             なお指定を消すのは不可。storeeelの枠は line-height: 0 を継承するため文字が潰れる。 */
          line-height: 1;
          letter-spacing: 0.08em;
        }
        /* 連携配信のセール商品ラベル。フィードフォルダのcontentは広告にできない（is_ad禁止）ため、
           広告バッジと同時に出ることはなく、同じ左上に置いてよい。
           ここの配色は色を選んでいない連携配信の既定値で、選ばれていればインラインstyleが勝つ */
        .storeeel-square-sale-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0 8px;
          /* 箱の高さは固定する。上下paddingやフォント本来の行送りに任せると、埋め込み先の
             font-family（バッジは指定を持たず継承する）で高さが変わり、サイトごとにラベルの
             大きさが違って見える。9px * 1.5em（line-height: normal の最大）でも収まる値。 */
          height: 14px;
          border-radius: 999px;
          background: #F5713A;
          z-index: 20;
          pointer-events: none;
        }
        .storeeel-square-sale-badge__text {
          color: #fff;
          font-size: 9px;
          font-style: normal;
          font-weight: 700;
          /* 行ボックスも font-size と同じ高さに固定する。normal だとフォント固有の行送りになり、
             上下非対称なぶんだけ文字が箱の中でずれる。1 にすれば上下の half-leading が均等に
             削られ、align-items: center と合わせて文字が箱の中央に来る。
             なお指定を消すのは不可。storeeelの枠は line-height: 0 を継承するため文字が潰れる。 */
          line-height: 1;
          letter-spacing: 0.08em;
        }
      `;
    } else {
      return `
        .storeeel-icon {
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
          width: var(--storeeel-circle-thumbnail-size, 80px);
          height: var(--storeeel-circle-thumbnail-size, 80px);
          border-radius: 50%;
          flex-shrink: 0;
          cursor: pointer;
        }
        /* 連携配信のSALEラベルを丸の内側へ重ねるための基準とクリップ。
           ルート要素（.storeeel_campaign_circle）にも .storeeel-icon が付いているため、
           サムネイル画像そのものだけに効くよう .storeeel-content-image と併せて指定する
           （ルートに overflow:hidden が乗るとグループ名が切れてしまう）。 */
        .storeeel-icon.storeeel-content-image {
          position: relative;
          overflow: hidden;
        }
        /* 既読状態に応じた外枠の色切り替え */
        .storeeel-group-border {
          border: var(--storeeel-read-status-ring-size, 4px) solid var(--storeeel-unseen-border-color, #000000);
          border-radius: 50%;
          padding: 3px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: fit-content;
        }
        .storeeel-group-border.storeeel-group-seen {
          border-color: var(--storeeel-seen-border-color, #A3A3A3);
        }
        .storeeel_campaign_circle {
          display: flex !important;
          width: var(--storeeel-circle-item-width, ${circleItemWidth});
          min-width: var(--storeeel-circle-item-width, ${circleItemWidth});
          flex: 0 0 var(--storeeel-circle-item-width, ${circleItemWidth});
          height: auto;
          min-height: 130px;
          flex-direction: column;
          justify-content: flex-start;
          gap: 7px;
          align-items: center;
          overflow: visible;
        }
        .storeeel_campaign_circle .storeeel-icon {
          border: var(--storeeel-circle-icon-border, ${circleIconBorder});
        }
        .storeeel_campaign_group_name {
          display: block;
          width: var(--storeeel-circle-item-width, ${circleGroupNameWidth});
          color: var(--storeeel-group-name-color, var(--Black-100, #272727));
          overflow: visible;
          white-space: pre-wrap;
          text-align: center;
          font-size: var(--storeeel-group-name-font-size, 12px);
          font-weight: var(--storeeel-group-name-font-weight, 500);
          font-family: var(--storeeel-group-name-font-family, inherit);
          line-height: 1.2;
          word-break: break-word;
          letter-spacing: 0px;
        }
        .storeeel_campaign_ad_label {
          display: block;
          width: 92px;
          text-align: center;
          color: #6b6b6b;
          font-size: 9px;
          font-style: normal;
          font-weight: 600;
          line-height: 1.1;
          letter-spacing: 0;
        }
        /* 連携配信のセール商品ラベル。丸いサムネイルの下部を横切る帯にする。
           左右いっぱい（left/right: 0）に敷き、親の border-radius: 50% + overflow: hidden で
           円弧に沿って切り取られるため、丸からはみ出さずに幅いっぱいの帯として見える。
           ここの配色は色を選んでいない連携配信の既定値（管理画面のSALEバッジと同じ）で、
           選ばれていればインラインstyleが勝つ。 */
        .storeeel_campaign_sale_label {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 6px;
          padding: 3px 0;
          background: #F5713A;
          color: #fff;
          text-align: center;
          font-size: 10px;
          font-style: normal;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.08em;
          white-space: nowrap;
          pointer-events: none;
          z-index: 2;
        }
      `;
    }
  }

  // タイル型は選択グリッドの列数(tileColumns)を維持しつつ、配置済みcontent_groupを
  // (tileRow, tileCol)昇順のフラット配列に並べ替えたうえで行優先に詰め直す（ADR-013）。
  // 元の行/列に欠番・空白があっても、詰めた結果は隙間なく並ぶ。
  const generateTileRows = (contentGroups) => {
    const markups = buildGroupMarkups(contentGroups).filter(group => group.html);
    if (markups.length === 0) return '';

    // (tileRow, tileCol) 昇順でフラットにソート（同値時は元の配列順＝position 昇順を維持する安定ソート）
    const sortedMarkups = [...markups].sort((a, b) => (
      a.tileRow - b.tileRow || a.tileCol - b.tileCol
    ));

    // 選択グリッドの列数（camelCase/snake_case 両対応、既定 4）
    const tileColumns = getCampaignTileColumns(campaign);

    // 列数ごとにフラット配列を再分割し、各チャンクを1行として描画する
    const rows = [];
    for (let i = 0; i < sortedMarkups.length; i += tileColumns) {
      rows.push(sortedMarkups.slice(i, i + tileColumns));
    }

    // タイル間・行間の gap（既存ヘルパー準拠、未設定時は既定 10px）
    const tileGap = getRenderedCampaignGroupGap(campaign) ?? TILE_DEFAULT_GAP;

    // 幅はアスペクト比から求まる自然な幅を使うが、横スクロールにはせず、列の持ち分
    // （行幅を列数で割った分）を超える場合はそちらで頭打ちにする（画面内に収まる大きさを
    // 常に幅の上限とするため）。高さは aspect-ratio により実際の幅から算出するため、
    // 頭打ちされても設定した縦横比は維持される（tile向けにthumbnailScaleは廃止、ADR-014）
    // 詰め後は最終行以外すべてtileColumns件になるため、列数はtileColumnsで統一してよい
    const naturalWidth = getCampaignTileNaturalWidth(campaign);
    const tileWidth = `min(${naturalWidth}px, calc((100% - ${(tileColumns - 1) * tileGap}px) / ${tileColumns}))`;
    // 行間 gap は親コンテナ(.storeeell_base_X)の column flex gap で付与するため、行自体には縦marginを付けない
    const rowStyle = `--storeeel-tile-width: ${tileWidth}; --storeeel-tile-gap: ${tileGap}px;`;

    return rows
      .map(groups => `<div class="storeeel-tile-row" style="${rowStyle}">${groups.map(g => g.html).join('')}</div>`)
      .join('');
  };

  // タイル型は行ラップ、それ以外は従来通り全 content_group を連結
  const body = isTile ? generateTileRows(contentGroups) : generateIcons(contentGroups);

  const iconsHTML = `
    ${body}
    <style>
      ${style()}
    </style>
  `;

  return iconsHTML;
}

export const setupVideoIntersectionObserver = (root = null) => {
  const ua = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipod|android.*mobile|windows phone/.test(ua);
  const directVideos = queryStoreeelAll('.storeeel-content-movie:not([data-storeeel-preview-video="true"])', root);
  const deferredContainers = queryStoreeelAll('[data-storeeel-video-src]', root);

  const bindPreviewLoop = (video) => {
    if (!video || video.dataset.storeeelPreviewLoopBound === 'true') return;
    video.dataset.storeeelPreviewLoopBound = 'true';
    video.loop = false;
    const getPreviewLoopCapSeconds = () => {
      const loopSeconds = getPreviewLoopSeconds();
      const duration = Number(video.duration);
      if (Number.isFinite(duration) && duration > 0) {
        return Math.min(loopSeconds, duration);
      }
      return loopSeconds;
    };
    const restartPreviewLoop = () => {
      video.currentTime = 0;
      if (!document.hidden && video.dataset.storeeelPreviewVisible === 'true') {
        const promise = video.play?.();
        if (promise?.catch) promise.catch(() => { void 0; });
      }
    };
    video.addEventListener('timeupdate', () => {
      const loopCapSeconds = getPreviewLoopCapSeconds();
      if (Number(video.currentTime) >= loopCapSeconds - PREVIEW_LOOP_EPSILON_SECONDS) {
        restartPreviewLoop();
      }
    });
    video.addEventListener('ended', restartPreviewLoop);
  };

  const playPreviewVideo = (video) => {
    if (!video || prefersReducedMotion() || document.hidden) return;
    bindPreviewLoop(video);
    video.dataset.storeeelPreviewVisible = 'true';
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    const duration = Number(video.duration);
    const loopCapSeconds = Number.isFinite(duration) && duration > 0
      ? Math.min(getPreviewLoopSeconds(), duration)
      : getPreviewLoopSeconds();
    if (video.currentTime >= loopCapSeconds) {
      video.currentTime = 0;
    }
    const promise = video.play?.();
    if (promise?.catch) promise.catch(() => { void 0; });
  };

  const pausePreviewVideo = (video, { reset = true } = {}) => {
    if (!video) return;
    video.dataset.storeeelPreviewVisible = 'false';
    video.pause?.();
    if (reset) video.currentTime = 0;
  };

  const handlePreviewVisibilityChange = () => {
    previewVideos.forEach((video) => {
      if (document.hidden) {
        video.dataset.storeeelPreviewVisibleBeforeHidden = video.dataset.storeeelPreviewVisible;
        pausePreviewVideo(video, { reset: false });
      } else if (video.dataset.storeeelPreviewVisibleBeforeHidden === 'true') {
        video.dataset.storeeelPreviewVisible = 'true';
        playPreviewVideo(video);
      }
    });
  };

  if (!document.__storeeelPreviewVisibilityBound) {
    document.addEventListener('visibilitychange', handlePreviewVisibilityChange);
    document.__storeeelPreviewVisibilityBound = true;
  }

  const showDeferredVideo = (container) => {
    const image = container.querySelector('[data-storeeel-preview-image="true"]');
    const video = container.querySelector('[data-storeeel-preview-video="true"]');
    const videoSrc = container.dataset.storeeelVideoSrc || '';
    if (!video || !videoSrc) return;
    previewVideos.add(video);
    if (prefersReducedMotion()) {
      hideDeferredVideo(container);
      return;
    }

    container.dataset.storeeelPlaybackActive = 'true';
    if (!video.getAttribute('src')) {
      video.src = `${videoSrc}#t=0.1`;
    }

    const revealVideo = () => {
      if (container.dataset.storeeelPlaybackActive !== 'true') return;
      video.style.display = 'block';
      if (image) image.style.display = 'none';
    };

    if (video.dataset.storeeelCanplayBound !== 'true') {
      video.addEventListener('loadeddata', revealVideo);
      video.addEventListener('canplay', revealVideo);
      video.dataset.storeeelCanplayBound = 'true';
    }

    if (video.readyState >= 2) {
      revealVideo();
    }

    video.currentTime = 0;
    playPreviewVideo(video);
  };

  const hideDeferredVideo = (container) => {
    const image = container.querySelector('[data-storeeel-preview-image="true"]');
    const video = container.querySelector('[data-storeeel-preview-video="true"]');
    if (!video) return;

    container.dataset.storeeelPlaybackActive = 'false';
    pausePreviewVideo(video);
    video.style.display = 'none';
    if (image) image.style.display = 'block';
  };

  if (prefersReducedMotion()) {
    directVideos.forEach(video => pausePreviewVideo(video));
    deferredContainers.forEach(container => hideDeferredVideo(container));
    return;
  }

  if (!isMobile) {
    // Desktop: hover to play/pause
    deferredContainers.forEach((container) => {
      if (container.dataset.storeeelHoverBound === 'true') return;
      container.addEventListener('mouseenter', () => {
        showDeferredVideo(container);
      });
      container.addEventListener('mouseleave', () => {
        hideDeferredVideo(container);
      });
      container.dataset.storeeelHoverBound = 'true';
    });

    directVideos.forEach(video => {
      // 再描画時の再実行でもイベント重複登録を避ける
      if (video.dataset.storeeelHoverBound === 'true') return;
      video.addEventListener('mouseenter', () => {
        previewVideos.add(video);
        video.currentTime = 0;
        playPreviewVideo(video);
      });
      video.addEventListener('mouseleave', () => {
        pausePreviewVideo(video);
      });
      video.dataset.storeeelHoverBound = 'true';
    });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const target = entry.target;
      if (target.dataset.storeeelVideoSrc) {
        if (entry.isIntersecting) {
          showDeferredVideo(target);
        } else {
          hideDeferredVideo(target);
        }
        return;
      }

      const video = entry.target;
      previewVideos.add(video);
      if (entry.isIntersecting) {
        video.currentTime = 0;
        playPreviewVideo(video);
      } else {
        pausePreviewVideo(video);
      }
    });
  }, {
    root: null,
    threshold: 0.9
  });

  // 再描画時の再実行でも同一要素を多重observeしない
  const pendingTargets = [...deferredContainers, ...directVideos].filter((target) => {
    if (target.dataset.storeeelObserved === 'true') return false;
    target.dataset.storeeelObserved = 'true';
    return true;
  });
  if (pendingTargets.length === 0) return;

  // スマホは動画の読み込みがサムネ画像と回線を取り合い、サムネの出揃いが遅れる。
  // ストーリー型・リール型・タイル型とも全サムネの読み込みを待ってから監視を始め、
  // その時点で画面内にあるものだけを再生する（observe 直後の初回コールバックで画面内判定が走る）。
  const thumbnailImages = queryStoreeelAll('[data-storeeel-preview-image="true"]', root);
  waitForThumbnailImages(thumbnailImages).then(() => {
    pendingTargets.forEach((target) => observer.observe(target));
  });
};
